import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  ApiEventSearch,
  RegisterReservationRequest,
  RegisterReservationResponse
} from 'extensions/st/piece-events/front/services/api-events.models';


const EMPTY_EVENT_SEARCH: ApiEventSearch = {
  benefits: [],
  pagination: {
    offset: 0,
    limit: 0,
    total: 0
  }
};


@Injectable()
export class ApiEventsService {

  // private static readonly IFRAME_SANDBOX = 'allow-forms allow-scripts allow-same-origin';
  private static readonly IFRAME_REFERRER_POLICY = 'strict-origin-when-cross-origin';

  constructor(
    private readonly http: HttpClient,
    private readonly sanitizer: DomSanitizer
  ) { }

  public search(
    lang: string,
    storeId: string,
    eventId?: string
  ): Observable<ApiEventSearch> {
    const url = this.requestUrl(lang, storeId, eventId);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    const requestOptions = { headers };
    return this.http.get<ApiEventSearch>(url, requestOptions).pipe(
      catchError(() => {
        return of(EMPTY_EVENT_SEARCH);
      })
    );
  }

  public registerReservation(
    lang: string,
    storeId: string,
    benefitId: string,
    accessToken: string | undefined,
    payload: RegisterReservationRequest
  ): Observable<RegisterReservationResponse> {
    const url = this.registerReservationUrl(lang, storeId, benefitId);
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return this.http.post<RegisterReservationResponse>(url, payload, { headers });
  }


  public getReservation(
    lang: string,
    storeId: string,
    accessToken: string | undefined
  ): Observable<ApiEventSearch> {
    const url = this.getReservationUrl(lang, storeId);
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return this.http.get<ApiEventSearch>(url, { headers }).pipe(
      catchError(() => {
        return of(EMPTY_EVENT_SEARCH);
      })
    );
  }

  public cancelReservation(
    lang: string,
    storeId: string,
    benefitId: string,
    accessToken: string | undefined
  ): Observable<void> {
    const url = this.registerReservationUrl(lang, storeId, benefitId);
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return this.http.delete<void>(url, { headers }).pipe(
      catchError(() => {
        return of(undefined);
      })
    );
  }

  public embed(embedCode: string): SafeHtml | '' {
    const normalizedIframe = this.normalizeIframe(embedCode);

    return normalizedIframe ? this.sanitizeIframe(normalizedIframe) : '';
  }

  /**
   * Request to the /benefits endpoint of the API
   * @param lang Language code
   * @param storeId Store Id
   * @param eventId Event Id
   * @returns Observable with the data returned by the api /benefits API endpoint
   */
  private requestUrl(lang: string, storeId: string, eventId?: string): string {
    const eventIdPath = eventId ? `/${eventId}` : '';
    const path = window.location.hostname.startsWith('www.stradivarius.com') || window.location.hostname.startsWith('axinstgeccwps1-stradivarius.central.inditex.grp') ? 'https://www.stradivarius.com' : 'https://preint-stradivariuscom.inditex.com';
    return `${path}/api/storefront/2/stores/${storeId}/benefits${eventIdPath}?expand=true&language=${lang}`;
  }


  /**
   * POST request to the /benefits/{benefitId}/reservations API endpoint to register a reservation.
   * @param lang Language code
   * @param storeId Store Id
   * @param benefitId Benefit Id
   * @returns Observable with the data returned by the api /benefits/{benefitId}/reservations API endpoint
   */
  private registerReservationUrl(lang: string, storeId: string, benefitId: string): string {
    const path = window.location.hostname.startsWith('www.stradivarius.com') || window.location.hostname.startsWith('axinstgeccwps1-stradivarius.central.inditex.grp') ? 'https://www.stradivarius.com' : 'https://preint-stradivariuscom.inditex.com';

    return `${path}/api/storefront/2/stores/${storeId}/benefits/${benefitId}/reservations?language=${lang}`;
  }


  /**
     * GET request to the /benefits/reservations API endpoint to get the user's reservations.
   * @param lang Language code
   * @param storeId Store Id
   * @returns Observable with the data returned by the api /benefits/reservations API endpoint
   */
  private getReservationUrl(lang: string, storeId: string): string {
    const path = window.location.hostname.startsWith('www.stradivarius.com') || window.location.hostname.startsWith('axinstgeccwps1-stradivarius.central.inditex.grp') ? 'https://www.stradivarius.com' : 'https://preint-stradivariuscom.inditex.com';

    return `${path}/api/storefront/2/stores/${storeId}/benefits/reservations?language=${lang}`;
  }

  private sanitizeIframe(iframe: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(iframe);
  }

  private normalizeIframe(embedCode: string): string {
    const trimmedEmbedCode = embedCode?.trim();
    if (!trimmedEmbedCode) {
      return '';
    }

    const template = document.createElement('template');
    template.innerHTML = trimmedEmbedCode;

    if (template.content.querySelector('script, object, embed, link, style')) {
      return '';
    }

    const elements = Array.from(template.content.children);
    if (elements.length !== 1 || elements[0].tagName.toLowerCase() !== 'iframe') {
      return '';
    }

    const iframe = elements[0] as HTMLIFrameElement;
    if (this.hasInlineEventHandlers(iframe) || iframe.hasAttribute('srcdoc')) {
      return '';
    }

    const src = this.normalizeIframeSrc(iframe.getAttribute('src'));
    if (!src) {
      return '';
    }

    const normalizedIframe = iframe.cloneNode(false) as HTMLIFrameElement;
    normalizedIframe.setAttribute('src', src);

    // Es necesario revisar este tema de seguridad relacionado con el atributo sandbox.
    // if (!normalizedIframe.hasAttribute('sandbox')) {
    //   normalizedIframe.setAttribute('sandbox', ApiEventsService.IFRAME_SANDBOX);
    // }

    if (!normalizedIframe.hasAttribute('referrerpolicy')) {
      normalizedIframe.setAttribute('referrerpolicy', ApiEventsService.IFRAME_REFERRER_POLICY);
    }

    return normalizedIframe.outerHTML;
  }

  private normalizeIframeSrc(src: string | null): string {
    const trimmedSrc = src?.trim();
    if (!trimmedSrc) {
      return '';
    }

    let url: URL;
    try {
      url = new URL(trimmedSrc, window.location.origin);
    } catch {
      return '';
    }

    if (url.protocol !== 'https:') {
      return '';
    }

    if (url.href.includes('www.studentbeans.com')) {
      const offerPath = url.searchParams.get('stb_offer_path');
      if (!offerPath) {
        return url.toString();
      }

      let offerUrl: URL;
      try {
        offerUrl = new URL(offerPath);
      } catch {
        return url.toString();
      }

      const pageQueryParams = new URLSearchParams(window.location.search);
      ['offer_uid', 'redeem_online'].forEach((param) => {
        const value = pageQueryParams.get(param);
        if (value !== null) {
          offerUrl.searchParams.set(param, param === 'redeem_online' ? value.replace(/"+$/, '') : value);
        }
      });

      if (pageQueryParams.has('offer_uid') || pageQueryParams.has('redeem_online')) {
        url.searchParams.set('stb_offer_path', offerUrl.toString());
      }
    }

    return url.toString();
  }

  private hasInlineEventHandlers(element: Element): boolean {
    return Array.from(element.attributes).some((attribute) => attribute.name.toLowerCase().startsWith('on'));
  }

}
